import config from '@deezer/next/services/config';

// Modules
import {ContentType} from '@deezer/next/services/types/contentType';
import type {ResponsiveValue} from '@tempo/core';

/**
 * Utility methods for building Thumbnail URLs and performing other actions that
 * are dependent on type
 */
const MAX_SERVER_WIDTH = 1500;
const MAX_SERVER_HEIGHT = 1500;
export const CoverTypeDefaultColor = '000000';
export const CoverTypeDefaultQuality = 80;
export const COVER_DEFAULT_MD5 = 'd41d8cd98f00b204e9800998ecf8427e';

export enum CoverType {
	COVER = 'cover',
	ARTIST = 'artist',
	PLAYLIST = 'playlist',
	USER = 'user',
	TALK = 'talk',
	MISC = 'misc',
}
export const getSrcWidth = (
	width: ResponsiveValue<number | string>,
): number => {
	if (typeof width === 'number') return width;
	if (typeof width === 'string') return parseInt(width, 10);
	const listOfWidths = Object.values(width) as string[] | number[];
	const numericWidths = listOfWidths.map(w =>
		typeof w === 'number' ? w : parseInt(w, 10),
	);
	return Math.max(...numericWidths);
};

export const getSrcHeight = (
	height: ResponsiveValue<number | string>,
): number => {
	if (typeof height === 'number') return height;
	if (typeof height === 'string') return parseInt(height, 10);
	const listOfHeights = Object.values(height) as string[] | number[];
	const numericHeights = listOfHeights.map(h =>
		typeof h === 'number' ? h : parseInt(h, 10),
	);
	return Math.max(...numericHeights);
};

export const getImageSrc = (
	coverType: CoverType = CoverType.MISC,
	md5: string,
	width: number,
	height: number,
	color = CoverTypeDefaultColor,
	quality = CoverTypeDefaultQuality,
	disableRetinaSupport = false,
): string => {
	if (typeof window === 'undefined') {
		return '';
	}

	color = /[0-9a-f]{6}|none/i.test(color) ? color : '000000';
	quality =
		Number.isInteger(quality) && quality > 0 && quality <= 100 ? quality : 80;
	const ext = color === 'none' ? 'png' : 'jpg';
	const domain = `https://${config.get('host_img_url_ssl')}`;

	// Support Retina Display
	if (window.devicePixelRatio > 1 && !disableRetinaSupport) {
		width = Math.round(width * window.devicePixelRatio);
		height = Math.round(height * window.devicePixelRatio);
		// Handle max image size delivered by the server
		if (width > MAX_SERVER_WIDTH || height > MAX_SERVER_HEIGHT) {
			const ratio = width / height;
			if (ratio > 1) {
				height = (height * MAX_SERVER_WIDTH) / width;
				width = MAX_SERVER_WIDTH;
			} else {
				width = (width * MAX_SERVER_HEIGHT) / height;
				height = MAX_SERVER_HEIGHT;
			}
		}
	}

	return `${domain}/${coverType}/${md5}/${height}x${width}-${color}-${quality}-0-0.${ext}`;
};

export const getCoverType = (type?: ContentType): CoverType => {
	switch (type) {
		case ContentType.Album:
		case ContentType.Track:
			return CoverType.COVER;
		case ContentType.Artist:
			return CoverType.ARTIST;
		case ContentType.Playlist:
			return CoverType.PLAYLIST;
		case ContentType.User:
			return CoverType.USER;
		case ContentType.Podcast:
		case ContentType.Episode:
			return CoverType.TALK;
		default:
			return CoverType.MISC;
	}
};

export const getDownloadedImage = (
	imageURL: string,
	filename: string,
	mimeType = 'image/jpg',
): Promise<void> => {
	return new Promise((resolve, reject) => {
		const downloadedImg = new Image();
		downloadedImg.crossOrigin = 'Anonymous';

		const onLoaded = () => {
			const canvas = document.createElement('canvas');
			const context = canvas.getContext('2d');

			if (!context) {
				reject(new Error('Cannot use context to download image'));
				return;
			}

			try {
				canvas.width = downloadedImg.width;
				canvas.height = downloadedImg.height;

				context.drawImage(downloadedImg, 0, 0);
				const imageBase64 = canvas.toDataURL(mimeType);
				const a = document.createElement('a');
				a.href = imageBase64;
				a.download = filename;
				a.click();
				resolve();
			} catch (e) {
				reject(e);
			}
		};

		downloadedImg.addEventListener('load', onLoaded, false);
		downloadedImg.src = imageURL;
	});
};

export const getClickURL = (type: ContentType, id = ''): string => {
	// Add to convert ID in Number
	id = Number(id) < 0 ? '' : id;
	return '/' + type + '/' + id;
};

export const getImageMd5 = (picture?: string): string => {
	return picture || COVER_DEFAULT_MD5;
};
